import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { api, cancelRequest } from "services/api.service";

const DEFAULT_MIN_CHARS = 3;
const DEFAULT_DEBOUNCE_MS = 300;

type UseDebouncedRemoteFilterSearchArgs = {
  query: string;
  enabled: boolean;
  cancelKey: string;
  path: string;
  filterColumn: string;
  responseKey: string;
  pageSize?: number;
  minChars?: number;
  debounceMs?: number;
};

export default function useDebouncedRemoteFilterSearch<T>({
  query,
  enabled,
  cancelKey,
  path,
  filterColumn,
  responseKey,
  pageSize,
  minChars = DEFAULT_MIN_CHARS,
  debounceMs = DEFAULT_DEBOUNCE_MS,
}: UseDebouncedRemoteFilterSearchArgs) {
  const trimmedQuery = query.trim();
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [results, setResults] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);

  const isRemoteSearch = enabled && trimmedQuery.length >= minChars;
  const isPending = isRemoteSearch && (loading || debouncedQuery !== trimmedQuery);

  useEffect(() => {
    if (!enabled) {
      setDebouncedQuery("");
      return;
    }

    const timeoutId = window.setTimeout(() => setDebouncedQuery(trimmedQuery), debounceMs);
    return () => window.clearTimeout(timeoutId);
  }, [enabled, trimmedQuery, debounceMs]);

  useEffect(() => {
    if (!enabled || debouncedQuery.length < minChars) {
      cancelRequest(cancelKey);
      setResults([]);
      setLoading(false);
      return;
    }

    let ignore = false;
    setLoading(true);

    const filterSegment = `${filterColumn},contains,${debouncedQuery}`;
    const params = new URLSearchParams({ [`filter[]`]: filterSegment });
    if (pageSize != null) {
      params.set("page[number]", "1");
      params.set("page[size]", String(pageSize));
    }

    api
      .get(`${path}?${params.toString()}`, { cancelKey } as any)
      .then((res) => {
        if (ignore) return;
        setResults((res.data?.[responseKey] as T[]) || []);
      })
      .catch((err) => {
        if (axios.isCancel(err) || ignore) return;
        setResults([]);
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [enabled, debouncedQuery, minChars, cancelKey, path, filterColumn, responseKey, pageSize]);

  const reset = useCallback(() => {
    cancelRequest(cancelKey);
    setResults([]);
    setLoading(false);
  }, [cancelKey]);

  return {
    results,
    isRemoteSearch,
    isPending,
    minChars,
    reset,
  };
}
